import { useMutation } from '@tanstack/react-query';
import { createStore, uploadStoreLogo, updateStoreLogoUrl } from '@dukanti/shared';
import type { Store, CreateStoreInput } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

export interface CreateStoreMutationInput {
  input: CreateStoreInput;
  logo: File | null;
}

/**
 * Mutation that orchestrates: create store -> upload logo -> update logo_url.
 *
 * Rev 2 fix: if logo upload fails after store creation, the mutation does
 * NOT throw. It returns the store (without logo) and sets a `logoFailed`
 * flag so the UI can show a warning toast. This avoids the dead-end where
 * retrying the form submission hits `slugTaken` on the user's own store.
 * The merchant can add a logo later in store settings.
 *
 * NOTE: admin access query hydration is NOT done here. The call site
 * (create-store-route) seeds the access cache before navigation so AuthGuard
 * sees the new membership immediately, then invalidates it after navigate().
 */
export function useCreateStore() {
  return useMutation<CreateStoreResult, unknown, CreateStoreMutationInput>({
    mutationFn: async ({ input, logo }) => {
      // Step 1: Create the store row
      const store = await createStore(supabase, input);

      // Step 2: Upload logo if provided
      if (logo) {
        try {
          const logoUrl = await uploadStoreLogo(supabase, store.id, logo);
          // Step 3: Update the store's logo_url
          await updateStoreLogoUrl(supabase, store.id, logoUrl);
          return { store: { ...store, logo_url: logoUrl }, logoFailed: false };
        } catch (uploadErr) {
          // Logo upload/update failed but store was created.
          // Rev 2: DON'T throw. Return the store without logo so the user
          // can proceed to /dashboard. A warning toast will inform them.
          console.error('Logo upload failed after store creation:', uploadErr);
          return { store, logoFailed: true };
        }
      }

      return { store, logoFailed: false };
    },
  });
}

/** Result type for useCreateStore, includes logo failure flag. */
export interface CreateStoreResult {
  store: Store;
  /** True if the store was created but logo upload failed. UI should
   *  show a warning toast, not an error that blocks navigation. */
  logoFailed: boolean;
}
